import { useState, type FormEvent } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { isPerfumeLockCode } from "@/models";
import { useTranslation } from "react-i18next";

// staff only: locks the formula once the staff code is typed. Clients must not lock their formula
// by themselves: only an admin can unlock it. See docs/formula-edit-protection.md
export function LockPerfumeWithCode({ onLock }: { onLock: () => Promise<void> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);

  const close = () => {
    setOpen(false);
    setCode("");
    setError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isPerfumeLockCode(code)) {
      setError(t("lockPerfumeWrongCode"));
      return;
    }
    setLocking(true);
    try {
      // the page then shows the locked, read-only formula
      await onLock();
      close();
    } catch (lockError) {
      console.error("Can't lock perfume", lockError);
      setError(t("lockPerfumeError"));
    } finally {
      setLocking(false);
    }
  };

  return (
    <>
      <Button
        color="secondary"
        size="small"
        startIcon={<LockOutlined />}
        onClick={() => setOpen(true)}
        sx={{ mt: 4 }}
      >
        {t("lockPerfume")}
      </Button>
      <Dialog open={open} onClose={close}>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{t("lockPerfume")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("lockPerfumeConfirmation")}</DialogContentText>
            <TextField
              autoFocus
              fullWidth
              type="password"
              autoComplete="off"
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
              label={t("lockPerfumeCode")}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(null);
              }}
              error={error !== null}
              helperText={error ?? " "}
              variant="standard"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>{t("cancel")}</Button>
            <Button type="submit" variant="contained" disabled={locking || code === ""}>
              {t("lockPerfume")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
